import { ref } from 'vue';
import {
  useQuery,
  useQueryClient,
  type QueryFunction,
  type QueryKey,
} from '@tanstack/vue-query';

type PollingQueryParams<T = unknown> = {
  queryKey: QueryKey;
  queryFn: QueryFunction<T>;
  shouldContinue: (data: T | undefined) => boolean;
  maxAttempts: number;
  interval: number;
  onPollingFinished?: (data: T | undefined) => void;
};

export class MaxPollAttemptsError extends Error {
  constructor() {
    super('ERR_MAX_POLL_ATTEMPTS');
  }
}

export const usePollingQuery = <T = unknown>({
  maxAttempts,
  interval,
  shouldContinue,
  onPollingFinished,
  ...options
}: PollingQueryParams<T>) => {
  const isPolling = ref(false);
  const attempt = ref(0);
  const isEnabled = ref(false);
  const queryClient = useQueryClient();

  const startPolling = () => {
    attempt.value = 0;
    isEnabled.value = true;
    isPolling.value = true;
  };

  const stopPolling = () => {
    // Cancel any active queries to prevent race conditions
    queryClient.cancelQueries({ queryKey: options.queryKey });

    isEnabled.value = false;
    isPolling.value = false;
  };

  const query = useQuery<T>({
    ...options,
    queryFn: context => {
      if (attempt.value >= maxAttempts) {
        isEnabled.value = false;
        isPolling.value = false;
        throw new MaxPollAttemptsError();
      }

      attempt.value++;
      return options.queryFn(context);
    },
    refetchInterval(query) {
      if (
        query.state.status === 'pending' ||
        (shouldContinue(query.state.data) && query.state.status === 'success')
      ) {
        return interval;
      }

      stopPolling();

      if (onPollingFinished) {
        onPollingFinished(query.state.data);
      }

      return false;
    },
    enabled: isEnabled,
    retry: false,
    staleTime: Infinity,
  });

  return { query, isPolling, startPolling, stopPolling, attempt };
};
